import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  ReactFlow,
  Background,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion, AnimatePresence } from "framer-motion"; // Add AnimatePresence
import startSound from "./assets/Mario 1 - Mario Riff.mp3";
import shoot from "./assets/fireball.mp3";
import explosion from "./assets/Break_Brick.mp3";
import gameWin from "./assets/game-win.mp3";
import gameOver from "./assets/die.mp3";
import GameOverModal from "./components/leaderboard/GameOverModal";

const TICK_MS = 200; // Slower game tick for ultra-slow movement
const BULLET_STEP = 25; // Slightly faster bullets to compensate for slower tick
const INV_STEP = 30; // Faster invader movement speed for better gameplay
const INV_DROP = 10; // Drop distance when hitting edge

const Button = ({ style = {}, children, ...rest }) => (
  <button
    {...rest}
    style={{ 
      padding: "8px 12px", 
      borderRadius: "4px", 
      backgroundColor: "#404040", 
      border: "none",
      color: "white",
      cursor: "pointer",
      fontSize: "14px",
      transition: "background-color 0.2s, transform 0.2s",
      ...style
    }}
    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#525252"; }}
    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#404040"; }}
    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.95)"; }}
    onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    onTouchStart={(e) => { e.currentTarget.style.transform = "scale(0.95)"; }}
    onTouchEnd={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
  >
    {children}
  </button>
);

/* ------------------------------ constants ------------------------------ */
const SOUNDS = {
  shoot: shoot,
  explosion: explosion,
  gameStart: startSound,
  gameOver: gameOver,
  gameWin: gameWin,
};

const loadSound = (url) => {
  const audio = new Audio(url);
  audio.load();
  return audio;
};

const getGameDimensions = () => {
  // Fixed arcade-style dimensions - same across all platforms
  const GAME_WIDTH = 600; // Set to container width
  const GAME_HEIGHT = 600; // Always 600px height on all platforms
  const cols = 6;
  const margin = 100; // Large margin for maximum movement space
  const invaderWidth = 25; // Smaller invaders for narrower space
  
  // Fixed spacing for consistent gameplay
  const spacingX = (GAME_WIDTH - (margin * 2) - invaderWidth) / (cols - 1);
  
  return {
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    playerY: GAME_HEIGHT - 40, // Fixed position near bottom
    invaderSpacingX: spacingX,
    invaderSpacingY: 45, // Tighter vertical spacing for mobile-friendly layout
    margin
  };
};

const dimensions = getGameDimensions();
const { width, height, playerY, invaderSpacingX, invaderSpacingY, margin } = dimensions;
const WIDTH = width;
const HEIGHT = height;
const PLAYER_Y = playerY;
const INV_COLS = 6; // Keep consistent across all devices
const INV_ROWS = 3; // Keep consistent across all devices
const INV_SPACING_X = invaderSpacingX;
const INV_SPACING_Y = invaderSpacingY;

/* ---------------------------------------------------------------------- */
const LevelTransition = ({ level }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 1.5 }}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 9999,
    }}
  >
    <motion.div
      initial={{ rotate: -10 }}
      animate={{ rotate: 10 }}
      transition={{ duration: 0.5, repeat: 3, repeatType: 'reverse' }}
      style={{
        padding: '2rem 3rem',
        backgroundColor: '#0f172a',
        border: '3px solid #38bdf8',
        borderRadius: '10px',
        boxShadow: '0 0 30px rgba(56, 189, 248, 0.6)',
        textAlign: 'center'
      }}
    >
      <h2 style={{
        fontFamily: "'Press Start 2P', monospace",
        fontSize: '24px',
        marginBottom: '16px',
        color: '#ffffff',
        textShadow: '0 0 10px rgba(56, 189, 248, 0.8)'
      }}>
        Level {level}
      </h2>
      <p style={{
        fontFamily: "'Press Start 2P', monospace",
        fontSize: '14px',
        color: '#f59e0b'
      }}>
        Get Ready!
      </p>
    </motion.div>
  </motion.div>
);

export default function SpaceInvaders() {
  const [nodes, setNodes] = useNodesState([]);
  const [invaders, setInvaders] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [playerX, setPlayerX] = useState(WIDTH / 2 - 20);
  const [dir, setDir] = useState(1);
  const [active, setActive] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1); // Add level state
  const [showLevelTransition, setShowLevelTransition] = useState(false); // Can be false or level number
  const levelRef = useRef(1); // Add level reference
  const scoreRef = useRef(0);
  const bulletsRef = useRef([]);
  const invadersRef = useRef([]);
  const dirRef = useRef(1);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [finalScore, setFinalScore] = useState(1000); // Set an initial score to display
  const [forceUpdate, setForceUpdate] = useState(0);
  const [shotsFired, setShotsFired] = useState(0);
  const shotsFiredRef = useRef(0);

  // Force a component re-render
  const forceRender = useCallback(() => {
    setForceUpdate((prev) => prev + 1);
  }, []);

  const lastShotRef = useRef(0);
  const SHOOT_COOLDOWN = 0; //150; //150; // Consistent cooldown for all devices

  /* ------------------------------ shooting ------------------------------ */
  const playSound = useCallback((soundName) => {
    const sound = soundsRef.current[soundName];
    if (sound) {
      sound.currentTime = 0;
      sound.play().catch((e) => console.log("Sound play error:", e));
    }
  }, []);

  const handleShoot = useCallback(() => {
    if (!active || showLevelTransition) return; // Prevent shooting during level transitions
    const now = Date.now();
    if (now - lastShotRef.current < SHOOT_COOLDOWN) return;
    lastShotRef.current = now;
    setShotsFired(s => {
      const newShots = s + 1;
      shotsFiredRef.current = newShots;
      return newShots;
    });
    setBullets((bs) => [
      ...bs,
      {
        id: `b-${now}`,
        position: { x: playerX + 14, y: PLAYER_Y - 24 },
        draggable: false,
        data: { label: "🔺" },
        style: {
          background: "#facc15",
          width: 12,
          height: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: 12,
          border: "none",
        },
      },
    ]);
    playSound("shoot");
  }, [active, playerX, playSound, showLevelTransition]); // Removed SHOOT_COOLDOWN - it's a constant

  /* ------------------------------ sounds ------------------------------ */
  const soundsRef = useRef({});
  useEffect(() => {
    Object.entries(SOUNDS).forEach(([key, url]) => {
      soundsRef.current[key] = loadSound(url);
    });
  }, []);

  /* -------------------------------- helpers ---------------------------- */
  // Calculate bonus based on shots fired efficiency
  const calculateEfficiencyBonus = (shots, invaderCount, level) => {
    // Perfect score would be one shot per invader
    const perfectScore = invaderCount;
    const maxBonus = 1000 * level; // Maximum bonus increases with level
    
    // If shots are less than or equal to perfect, award max bonus
    if (shots <= perfectScore) {
      return maxBonus;
    }
    
    // Calculate efficiency ratio
    const efficiency = perfectScore / shots;
    // Bonus decreases as efficiency decreases
    const bonus = Math.floor(maxBonus * efficiency);
    
    // Minimum bonus is 10% of max if at least one invader was destroyed
    return Math.max(Math.floor(maxBonus * 0.1), bonus);
  };

  // Helper to calculate invader point value based on row and level
  const calculateInvaderPoints = (row, level, isSpecial = false) => {
    const rowBonus = Math.max(3 - row, 0) * 50;
    const levelBonus = (level - 1) * 25;
    const specialBonus = isSpecial ? 100 : 0;
    return 100 + rowBonus + levelBonus + specialBonus;
  };
  
  const makePlayer = useCallback(
    (x) => ({
      id: "player",
      position: { x, y: PLAYER_Y },
      draggable: false,
      data: { label: "🚀" },
      style: {
        background: "#0369a1",
        width: 30, // Smaller for mobile-friendly size
        height: 30,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 18, // Smaller emoji
        color: "white",
        border: "none",
      },
    }),
    []
  );

  /* --------------------------------- init ----------------------------- */
  const initGame = (startNewGame = true) => {
    // CRITICAL: Never reset score if we're in a level transition
    if (showLevelTransition && startNewGame) {
      startNewGame = false;
    }
    
    // Get the current level value from ref (most reliable)
    const currentLevel = startNewGame ? 1 : levelRef.current;
    
    // Only reset game state for new games
    if (startNewGame) {
      setLevel(1);
      levelRef.current = 1;
      setShotsFired(0);
      shotsFiredRef.current = 0;
      setScore(0);
      scoreRef.current = 0;
    } else {
      // Store the score value to preserve it - this is the authoritative source
      const preservedScore = scoreRef.current;
      
      // Make sure state matches refs for level continuity
      setLevel(currentLevel);
      
      // Force the score state to match the preserved ref value
      setScore(preservedScore);
      
      // Ensure the ref maintains the correct value
      scoreRef.current = preservedScore;
      
      // Don't reset shots when continuing - they were already reset in level completion
    }
    
    // Calculate difficulty based on current level
    const invadersPerRow = Math.min(INV_COLS + Math.floor(currentLevel / 2), 14); // Add more invaders per level (max 14)
    const invaderRows = Math.min(INV_ROWS + Math.floor(currentLevel / 3), 6); // Add more rows per level (max 6)
    const invaderSpeed = INV_STEP + Math.floor(currentLevel / 3); // Slower progression - increase speed every 3 levels instead of 2
    
    const inv = [];
    let id = 0;
    for (let r = 0; r < invaderRows; r++) {
      for (let c = 0; c < invadersPerRow; c++) {
        // Determine invader type based on row and level
        const isSpecial = (currentLevel > 3 && r < 2) || (currentLevel > 2 && r === 0);
        const isElite = currentLevel > 4 && r === 0;
        const emoji = isElite ? "👾" : isSpecial ? "👿" : "👽";
        
        // Calculate point value for display in tooltip
        const pointValue = calculateInvaderPoints(r, currentLevel, isSpecial || isElite);
        
        const xPos = margin + c * ((WIDTH - 2 * margin) / (invadersPerRow - 1));

        inv.push({
          id: `inv-${id++}`,
          position: { 
            x: xPos, 
            y: 60 + r * INV_SPACING_Y 
          },
          draggable: false,
          data: { 
            label: emoji,
            pointValue: pointValue
          },
          style: {
            background: isElite ? "#7e22ce" : isSpecial ? "#b91c1c" : "#0369a1",
            width: 25,
            height: 25,
            borderRadius: 6,
            fontSize: 16,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            border: "none",
            boxShadow: isElite ? "0 0 10px #a855f7" : isSpecial ? "0 0 5px #ef4444" : "none",
          },
        });
      }
    }
    
    setInvaders(inv);
    setBullets([]);
    setPlayerX(WIDTH/2); // Center player
    setDir(1);
    
    setActive(true);
    setGameOver(false);
    setGameWon(false);
    setFinalScore(0);
    
    // Only play start sound for new games
    if (startNewGame) {
      playSound("gameStart");
    }
  };

  /* ------------------------------ controls ----------------------------- */
  useEffect(() => {
    if (!active || showLevelTransition) return; // Prevent controls during level transitions

    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        setPlayerX((x) => {
          const newX = Math.max(0, x - 20);
          return newX;
        });
      } else if (e.key === "ArrowRight") {
        setPlayerX((x) => {
          const newX = Math.min(WIDTH - 30, x + 20);
          return newX;
        });
      } else if (e.key === " " || e.key === "ArrowUp") {
        e.preventDefault();
        handleShoot();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, handleShoot, showLevelTransition]); // Add showLevelTransition to dependencies

  /* ------------------------------ keep refs in sync --------------------------- */
  useEffect(() => {
    // Only update scoreRef if we're not in the middle of a level transition
    // This prevents the ref from being overwritten during score preservation
    if (!showLevelTransition) {
      scoreRef.current = score;
    }
  }, [score, showLevelTransition]);
  
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    bulletsRef.current = bullets;
  }, [bullets]);

  useEffect(() => {
    invadersRef.current = invaders;
  }, [invaders]);

  useEffect(() => {
    dirRef.current = dir;
  }, [dir]);

  /* ------------------------------ game loop --------------------------- */
  useEffect(() => {
    if (!active) return;
    
    // Calculate invader speed based on current level from ref
    const currentLevel = levelRef.current;
    const invaderSpeed = INV_STEP + Math.floor(currentLevel / 3); // Increase speed every 3 levels
    const invaderDropDistance = INV_DROP + Math.floor(currentLevel / 2); // Increase drop distance every 2 levels
    
    const loop = setInterval(() => {
      // Get current values from refs to avoid stale closures
      const currentBullets = bulletsRef.current;
      const currentInvaders = invadersRef.current;
      const currentDir = dirRef.current;

      /* Move bullets */
      const movedBullets = currentBullets
        .map((b) => ({ ...b, position: { x: b.position.x, y: b.position.y - BULLET_STEP } }))
        .filter((b) => b.position.y > -30);

      /* Move invaders */
      const edgeHit = currentInvaders.some(
        (n) => n.position.x + currentDir * invaderSpeed < 10 || n.position.x + currentDir * invaderSpeed > WIDTH - 50
      );
      const newDir = edgeHit ? currentDir * -1 : currentDir;
      const movedInvaders = currentInvaders.map((n) => ({
        ...n,
        position: {
          x: n.position.x + newDir * invaderSpeed,
          y: n.position.y + (edgeHit ? invaderDropDistance : 0),
        },
      }));

      /* Collision detection */
      const remainingInvaders = [];
      let hitCount = 0;
      let hitPoints = 0; // Track points separately from count
      
      movedInvaders.forEach((inv) => {
        const hit = movedBullets.some((b) => {
          // Improved collision detection with proper center-point calculation
          const bulletCenterX = b.position.x + 6; // Bullet width is 12, so center is +6
          const bulletCenterY = b.position.y + 10; // Bullet height is 20, so center is +10
          const invaderCenterX = inv.position.x + 12.5; // Invader width is 25, so center is +12.5
          const invaderCenterY = inv.position.y + 12.5; // Invader height is 25, so center is +12.5
          
          // Distance-based collision detection for more accurate results
          const distanceX = Math.abs(bulletCenterX - invaderCenterX);
          const distanceY = Math.abs(bulletCenterY - invaderCenterY);
          
          // Allow for some overlap tolerance (collision happens when centers are close enough)
          const collisionThresholdX = 15; // Half bullet width + half invader width + tolerance
          const collisionThresholdY = 15; // Half bullet height + half invader height + tolerance
          
          const collision = distanceX <= collisionThresholdX && distanceY <= collisionThresholdY;
          
          return collision;
        });
        
        if (!hit) {
          remainingInvaders.push(inv);
        } else {
          hitCount += 1;
          
          // Use the point value stored in the invader's data
          // If not available (for backwards compatibility), calculate it
          const points = inv.data.pointValue || 100;
          hitPoints += points;
        }
      });

      if (hitCount) {
        setScore((s) => {
          const newScore = s + hitPoints;
          scoreRef.current = newScore; // Update ref immediately
          return newScore;
        });
        playSound("explosion");
      }

      /* Remove bullets that hit */
      const survivingBullets = movedBullets.filter((b) => {
        return !movedInvaders.some((inv) => {
          // Only check against invaders that were hit (i.e., NOT in remaining invaders)
          if (remainingInvaders.find(ri => ri.id === inv.id)) return false;
          
          // Use same collision detection logic as above
          const bulletCenterX = b.position.x + 6; 
          const bulletCenterY = b.position.y + 10; 
          const invaderCenterX = inv.position.x + 12.5; 
          const invaderCenterY = inv.position.y + 12.5; 
          
          const distanceX = Math.abs(bulletCenterX - invaderCenterX);
          const distanceY = Math.abs(bulletCenterY - invaderCenterY);
          
          const collisionThresholdX = 15;
          const collisionThresholdY = 15;
          
          return distanceX <= collisionThresholdX && distanceY <= collisionThresholdY;
        });
      });

      // Update state
      setBullets(survivingBullets);
      setInvaders(remainingInvaders);
      setDir(newDir);

      /* Win / lose checks */
      if (remainingInvaders.some((n) => n.position.y >= PLAYER_Y - 20)) {
        // Game over sequence
        const final = scoreRef.current;
        setActive(false);
        setGameOver(true);
        setFinalScore(final);
        
        // Make sure we see the modal by forcing a focus
        setTimeout(() => {
          document.body.style.overflow = "hidden"; // Prevent scrolling behind modal
          
          // Force re-render
          forceRender();
        }, 100);
        
        playSound("gameOver");
      }
      if (remainingInvaders.length === 0) {
        // Level completed sequence!
        playSound("gameWin");
        
        // Get current level from ref (most reliable source)
        const currentLevelValue = levelRef.current;
        
        // Calculate efficiency bonus using current level values
        const totalInvaders = currentLevelValue <= 2 ? INV_COLS * INV_ROWS : 
                            Math.min(INV_COLS + Math.floor(currentLevelValue / 2), 14) * 
                            Math.min(INV_ROWS + Math.floor(currentLevelValue / 3), 6);
        const efficiencyBonus = calculateEfficiencyBonus(shotsFiredRef.current, totalInvaders, currentLevelValue);
        
        // Update score with bonus immediately in ref and state
        const currentScore = scoreRef.current;
        const newScoreWithBonus = currentScore + efficiencyBonus;
        
        // Update both ref and state immediately and synchronously
        scoreRef.current = newScoreWithBonus;
        setScore(newScoreWithBonus);
        
        // Calculate next level
        const nextLevel = currentLevelValue + 1;
        
        // Pause game first
        setActive(false);
        
        // Reset shots counter
        setShotsFired(0);
        shotsFiredRef.current = 0;
        
        // Show level transition with next level number
        setShowLevelTransition(nextLevel); // Pass the next level number directly
        
        // Start next level after transition
        const startNextLevel = () => {
          // Store score before any state changes
          const currentScoreToPreserve = scoreRef.current;
          
          setShowLevelTransition(false);
          
          // Update level in ref and state before calling initGame
          levelRef.current = nextLevel;
          setLevel(nextLevel);
          
          // Use setTimeout to ensure state updates are processed
          setTimeout(() => {
            // Pass false to initGame to preserve score and level
            initGame(false);
          }, 50);
        };
        
        setTimeout(startNextLevel, 3000);
      }
    }, TICK_MS);

    return () => clearInterval(loop);
  }, [active, playSound, forceRender]); // Removed unnecessary dependencies - using refs for current values

  const playerNode = useMemo(() => makePlayer(playerX), [makePlayer, playerX]);

  useEffect(() => {
    // Add tooltips to invaders showing their point value
    const nodesWithTooltips = [
      playerNode,
      ...invaders.map(inv => ({
        ...inv,
        // Show point tooltips on all devices
        data: {
          ...inv.data,
          title: `${inv.data.pointValue} pts`
        }
      })),
      ...bullets
    ];
    setNodes(nodesWithTooltips);
  }, [playerNode, invaders, bullets, setNodes]);

  /* ------------------------------- render ----------------------------- */
  return (
    <>
      {/* Outer container for centering */}
      <div style={{ 
        height: "100vh", 
        width: "100vw", 
        display: "flex", 
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#000000",
        padding: "10px", // Small padding to prevent edge touching
        margin: 0,
        position: "fixed",
        top: 0,
        left: 0
      }}>
        {/* Fixed-size game container */}
        <div style={{ 
          width: "300px", 
          height: "600px", 
          minWidth: "300px",  // Prevent shrinking
          maxWidth: "300px",  // Prevent growing
          display: "flex", 
          flexDirection: "column", 
          color: "white", 
          overflow: "hidden",
          backgroundColor: "#000000",
          border: "2px solid #38bdf8",
          borderRadius: "10px",
          boxShadow: "0 0 20px rgba(56, 189, 248, 0.3)"
        }}>
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          style={{
            textAlign: "center",
            backgroundColor: "#0f172a",
            padding: "8px 0",
            borderBottom: "2px solid #38bdf8",
            boxShadow: "0 4px 12px rgba(56, 189, 248, 0.4)",
            fontFamily: "'Press Start 2P', monospace, system-ui",
            flexShrink: 0
          }}
        >
          <h1 style={{
            margin: 0,
            fontSize: "16px", // Smaller for fixed container
            background: "linear-gradient(to right, #38bdf8, #818cf8)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
            letterSpacing: "1px"
          }}>
            SPACE INVADERS
          </h1>
        </motion.div>
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          style={{
            padding: "8px 12px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
            backgroundColor: "#000000",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
            borderBottom: "1px solid rgb(51, 51, 51)",
            flexShrink: 0
          }}
        >
          <Button type="button" onClick={(e) => { 
            e.preventDefault(); 
            if (showLevelTransition) {
              return;
            }
            initGame(); 
          }}>
            {active ? "Restart" : "Start"}
          </Button>
          
          <div style={{ marginLeft: "auto", display: "flex", gap: "12px" }}>
            <span style={{ fontSize: "10px", fontWeight: "600", color: "#3b82f6" }}>Level: {level}</span>
            <span style={{ fontSize: "10px", fontWeight: "600", color: "green" }}>Score: {score}</span>
          </div>
        </motion.div>

        <div style={{ 
          flex: 1, 
          position: "relative", 
          width: "100%", 
          backgroundColor: "#000000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "0" // No padding needed in fixed container
        }}>
          
          <div style={{ 
            width: "100%", // Fill the container width (350px)
            height: "400px", // Fixed height for game area
            background: "#000000",
            overflow: "hidden",
            position: "relative"
          }}>            
            <ReactFlow
              nodes={nodes}
              edges={[]}
              panOnDrag={false}
              zoomOnScroll={false}
              panOnScroll={false}
              minZoom={0.5}
              maxZoom={0.5}
              defaultViewport={{
                x: 0,
                y: 0,
                zoom: 0.5, // Use full zoom to utilize the 300px width
              }}
              style={{ width: "100%", height: "100%" }}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={40} size={1.5} color="#333333" variant="lines" style={{ opacity: 0.4 }} />
            </ReactFlow>
          </div>

          {/* Touch Controls - Available on all platforms */}
          <div style={{
            width: "100%", // Full width of container
            height: "80px", // Fixed height for controls
            backgroundColor: "#000000", 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center",
            borderTop: "1px solid #38bdf8",
            position: "relative",
            zIndex: 20
          }}>
            <div style={{
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              width: "100%",
              maxWidth: "280px",
              padding: "0 10px"
            }}>
              <button
                onTouchStart={() => {
                  if (showLevelTransition) return; // Prevent touch controls during transitions
                  setPlayerX((x) => {
                    const newX = Math.max(0, x - 20);
                    return newX;
                  });
                }}
                onTouchEnd={(e) => e.preventDefault()}
                onClick={() => {
                  if (showLevelTransition) return; // Prevent click controls during transitions
                  setPlayerX((x) => {
                    const newX = Math.max(0, x - 20);
                    return newX;
                  });
                }}
                style={{ 
                  width: "50px", 
                  height: "50px", 
                  borderRadius: "12px", 
                  backgroundColor: "rgba(56, 189, 248, 0.9)", 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center", 
                  color: "white", 
                  fontSize: "0px", // Hide text, use pseudo-element for arrow
                  fontWeight: "bold",
                  border: "3px solid #0ea5e9",
                  padding: 0,
                  touchAction: "manipulation",
                  cursor: "pointer",
                  boxShadow: "0 6px 16px rgba(56, 189, 248, 0.5)",
                  transition: "all 0.2s ease",
                  position: "relative",
                  background: "linear-gradient(135deg, rgba(56, 189, 248, 0.9), rgba(14, 165, 233, 0.9))"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "rgba(56, 189, 248, 1)";
                  e.target.style.transform = "scale(1.1)";
                  e.target.style.boxShadow = "0 8px 20px rgba(56, 189, 248, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "rgba(56, 189, 248, 0.9)";
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 6px 16px rgba(56, 189, 248, 0.5)";
                }}
              >
                <div style={{
                  width: 0,
                  height: 0,
                  borderTop: "10px solid transparent",
                  borderBottom: "10px solid transparent",
                  borderRight: "14px solid white",
                  marginLeft: "-2px"
                }} />
              </button>
              <button
                onPointerDown={(e) => {
                  if (showLevelTransition) return; // Prevent shooting during transitions
                  handleShoot();
                }}
                onTouchStart={(e) => {
                  if (showLevelTransition) return; // Prevent shooting during transitions
                  handleShoot();
                }}
                onClick={(e) => {
                  if (showLevelTransition) return; // Prevent shooting during transitions
                  handleShoot();
                }}
                style={{ 
                  width: "50px", 
                  height: "50px", 
                  borderRadius: "12px", 
                  backgroundColor: "rgba(239, 68, 68, 0.9)", 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center", 
                  color: "white", 
                  fontSize: "20px", 
                  fontWeight: "bold",
                  border: "3px solid #dc2626",
                  padding: 0,
                  touchAction: "manipulation",
                  cursor: "pointer",
                  boxShadow: "0 6px 16px rgba(239, 68, 68, 0.5)",
                  transition: "all 0.2s ease",
                  background: "linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.9))"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "rgba(239, 68, 68, 1)";
                  e.target.style.transform = "scale(1.1)";
                  e.target.style.boxShadow = "0 8px 20px rgba(239, 68, 68, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "rgba(239, 68, 68, 0.9)";
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 6px 16px rgba(239, 68, 68, 0.5)";
                }}
              >
                🚀
              </button>
              <button
                onTouchStart={() => {
                  if (showLevelTransition) return; // Prevent touch controls during transitions
                  setPlayerX((x) => {
                    const newX = Math.min(WIDTH - 30, x + 20);
                    return newX;
                  });
                }}
                onTouchEnd={(e) => e.preventDefault()}
                onClick={() => {
                  if (showLevelTransition) return; // Prevent click controls during transitions
                  setPlayerX((x) => {
                    const newX = Math.min(WIDTH - 30, x + 20);
                    return newX;
                  });
                }}
                style={{ 
                  width: "50px", 
                  height: "50px", 
                  borderRadius: "12px", 
                  backgroundColor: "rgba(56, 189, 248, 0.9)", 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center", 
                  color: "white", 
                  fontSize: "0px", // Hide text, use pseudo-element for arrow
                  fontWeight: "bold",
                  border: "3px solid #0ea5e9",
                  padding: 0,
                  touchAction: "manipulation",
                  cursor: "pointer",
                  boxShadow: "0 6px 16px rgba(56, 189, 248, 0.5)",
                  transition: "all 0.2s ease",
                  position: "relative",
                  background: "linear-gradient(135deg, rgba(56, 189, 248, 0.9), rgba(14, 165, 233, 0.9))"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "rgba(56, 189, 248, 1)";
                  e.target.style.transform = "scale(1.1)";
                  e.target.style.boxShadow = "0 8px 20px rgba(56, 189, 248, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "rgba(56, 189, 248, 0.9)";
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 6px 16px rgba(56, 189, 248, 0.5)";
                }}
              >
                <div style={{
                  width: 0,
                  height: 0,
                  borderTop: "10px solid transparent",
                  borderBottom: "10px solid transparent",
                  borderLeft: "14px solid white",
                  marginRight: "-2px"
                }} />
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Level transition overlay */}
      <AnimatePresence>
        {showLevelTransition && <LevelTransition level={showLevelTransition} />}
      </AnimatePresence>
      
      {(gameOver || gameWon) && (
        <GameOverModal
          score={finalScore}
          level={level} // Pass level to GameOverModal
          isWin={gameWon}
          onRestart={() => {
            // Reset body overflow and remove debug element when restarting
            document.body.style.overflow = "auto";
            
            initGame();
          }}
          onClose={() => {
            // Reset body overflow and remove debug element when closing
            document.body.style.overflow = "auto";

            setActive(false); // Ensure game is not active
            setGameOver(false);
            setGameWon(false);
          }}
        />
      )}
    </>
  );
}
